<template>
    <div :class="['tournament-card', 'tournament-card--' + cardType, { 'tournament-card--hide-tag': hideTagHeader }]" :data-tournament-id="tournamentId">
        <div v-if="!hideTagHeader" :class="'tag-header tag-header--' + getDisplayMessageColor()">
            <span class="tag-header__tag"><i v-if="getDisplayMessage().icon" :class="getDisplayMessage().icon"></i>{{
                getDisplayMessage().text }}</span>
        </div>

        <!-- 3-dot menu -->
        <div v-if="hideTagHeader" class="card-menu-container">
            <v-menu v-if="hasMenuActions" offset-y left>
                <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on" @click.stop class="card-menu-dots">
                        <i class="fa-solid fa-ellipsis"></i>
                    </span>
                </template>
                <v-list dense class="card-menu-list">
                    <v-list-item v-if="showArchiveButton" @click="handleArchiveClick" :disabled="archiving">
                        <v-list-item-icon class="mr-2"><v-icon small>fa-regular fa-archive</v-icon></v-list-item-icon>
                        <v-list-item-title>{{ $t('archiveTournament') }}</v-list-item-title>
                    </v-list-item>
                    <v-list-item v-if="showUnarchiveButton" @click="handleUnarchiveClick" :disabled="archiving">
                        <v-list-item-icon class="mr-2"><v-icon small>fa-regular fa-box-open</v-icon></v-list-item-icon>
                        <v-list-item-title>{{ $t('unarchiveTournament') }}</v-list-item-title>
                    </v-list-item>
                </v-list>
            </v-menu>
            <span v-else class="card-menu-dots card-menu-dots--disabled" @click.stop>
                <i class="fa-solid fa-ellipsis"></i>
            </span>
        </div>

        <h2 class="tournament-card-card__title" @click="goToTournament(tournamentId)">
            {{ tournamentTitle }}
        </h2>
        <div class="card-divider" v-if="cardType == 'allVariation'"></div>
        <div class="tournament-info" v-if="cardType == 'allVariation'" @click="goToTournament(tournamentId)">
            <span class="tournament-info__text" v-for="(info, i) in tournamentInfo" :key="i"><i
                    :class="'tournament-info__icon ' + info.icon"></i>{{ info.text }}</span>
        </div>
        <div :class="'tournament-timer tournament-timer--' + timerState.type" @click="goToTournament(tournamentId)">
            <Fragment v-if="cardType == 'allVariation' && !hasEnded">
                <div :class="'timer-state timer-box' + ' ' + timerState.color">
                    {{ timerState.text }}
                </div>
                <div v-for="(el, i) in timerFormatted" :key="i">
                    <span :class="'tournament-timer__number tournament-timer__' + el.indicates + ' timer-box'">
                        {{ el.value }}<br><span>{{ el.indicates }}</span>
                    </span>
                </div>
            </Fragment>
            <Fragment v-else-if="cardType == 'enrolledVariation' && !hasEnded">
                <i class="fa-regular fa-clock"></i>{{ timerState.text }} &nbsp;<span v-for="(el, i) in timerFormatted"
                    :key="i">{{ el.value }}{{ el.indicates.charAt(0) }}<span class="dots" v-if="i < 3">:</span></span>
            </Fragment>
            <p v-else class="tournament-timer__ended-text"><i v-if="cardType == 'enrolledVariation'"
                    class="fa-regular fa-clock"></i>{{ timerState.text }}</p>
        </div>

        <!-- Archive/Unarchive confirmation dialogs -->
        <v-dialog v-model="showArchiveDialog" max-width="400px">
            <v-card>
                <v-card-title class="text-h6">{{ $t('archiveTournament') }}</v-card-title>
                <v-card-text>
                    {{ $t('confirmArchiveTournament') }}
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="showArchiveDialog = false">{{ $t('btnCancel') }}</v-btn>
                    <v-btn color="primary" @click="confirmArchive" :loading="archiving">{{ $t('archiveTournament') }}</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="showUnarchiveDialog" max-width="400px">
            <v-card>
                <v-card-title class="text-h6">{{ $t('unarchiveTournament') }}</v-card-title>
                <v-card-text>
                    {{ $t('confirmUnarchiveTournament') }}
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="showUnarchiveDialog = false">{{ $t('btnCancel') }}</v-btn>
                    <v-btn color="primary" @click="confirmUnarchive" :loading="archiving">{{ $t('unarchiveTournament') }}</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { Fragment } from 'vue-fragment';
import Repository from '../../../repositories/tournament';

export default {
    name: "TournamentCard",
    components: {
        Fragment
    },
    props: {
        cardType: String,
        tournamentMsgState: Object,
        tournamentTitle: String,
        tournamentStartDate: Date,
        tournamentEndDate: Date,
        tournamentInfo: Array,
        tournamentActive: Boolean,
        tournamentInProgress: Boolean,
        tournamentUserEnrolled: Boolean,
        tournamentId: Number,
        // New props for archiving functionality
        showArchiveOption: {
            type: Boolean,
            default: false
        },
        isFinished: {
            type: Boolean,
            default: false
        },
        tournamentVisibility: {
            type: String,
            default: 'PUBLIC'
        },
        hideTagHeader: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            timerState: { type: "box-type", text: "BEGINS IN", color: "bg-yellow" },
            timerFormatted: [{ value: "00", indicates: "day" }, { value: "00", indicates: "hour" }, { value: "00", indicates: "min" }, { value: "00", indicates: "sec" }],
            hasEnded: false,
            // Archive functionality
            showArchiveDialog: false,
            showUnarchiveDialog: false,
            archiving: false
        }
    },
    computed: {
        showArchiveButton() {
            // Show archive button only if:
            // 1. Archive option is enabled (club tournaments view)
            // 2. Tournament is finished
            // 3. User is admin or founder
            // 4. Tournament is not already archived
            return this.showArchiveOption && 
                   this.isFinished && 
                   this.isUserAdmin && 
                   this.tournamentVisibility !== 'ARCHIVED';
        },
        showUnarchiveButton() {
            // Show unarchive button only if:
            // 1. Archive option is enabled (club tournaments view)
            // 2. Tournament is finished
            // 3. User is admin or founder
            // 4. Tournament is currently archived
            return this.showArchiveOption && 
                   this.isFinished && 
                   this.isUserAdmin && 
                   this.tournamentVisibility === 'ARCHIVED';
        },
        isUserAdmin() {
            return this.$store.state.clubMember &&
                   (this.$store.state.clubMember.rol === 'ADMIN' || this.$store.state.clubMember.rol === 'FOUNDER');
        },
        hasMenuActions() {
            return this.showArchiveButton || this.showUnarchiveButton;
        }
    },
    mounted() {
        this.setTimeForCard();
    },
    methods: {
        setTimeForCard() {
            let self = this;
            if (this.tournamentActive) {
                this.hasEnded = false
                let compareWithDate = undefined;
                if (this.tournamentInProgress) {
                    this.timerState = {
                        type: this.cardType == "allVariation" ? "box-type" : "no-box-type",
                        text: this.$t("endsIn"),
                        color: this.tournamentUserEnrolled ? "bg-green" : "bg-orange"
                    }
                    compareWithDate = this.tournamentEndDate;
                } else {
                    this.timerState = {
                        type: this.cardType == "allVariation" ? "box-type" : "no-box-type",
                        text: this.tournamentUserEnrolled ? this.$t("beginsIn") : this.$t("stillOpen"),
                        color: "bg-yellow"
                    }
                    compareWithDate = this.tournamentStartDate;
                }
                if (this.cardType == "allVariation") {
                    this.timerState.text = this.timerState.text.toUpperCase();
                }
                setInterval(() => {
                    let remainingTime = self.dhm(compareWithDate - new Date()).split(":");
                    self.timerFormatted = [{
                        value: Number(remainingTime[0]).toLocaleString('en-US', {
                            minimumIntegerDigits: 2,
                            useGrouping: false
                        }), indicates: this.$t("day")
                    }, {
                        value: Number(remainingTime[1]).toLocaleString('en-US', {
                            minimumIntegerDigits: 2,
                            useGrouping: false
                        }), indicates: this.$t("hour")
                    }, {
                        value: Number(remainingTime[2]).toLocaleString('en-US', {
                            minimumIntegerDigits: 2,
                            useGrouping: false
                        }), indicates: this.$t("minutes")
                    }, {
                        value: Number(remainingTime[3]).toLocaleString('en-US', {
                            minimumIntegerDigits: 2,
                            useGrouping: false
                        }), indicates: this.$t("seconds")
                    },]
                }, 1000)

            } else {
                this.timerState.text = this.$t("tournamentEnded");
                this.hasEnded = true;
            }
        },

        handleArchiveClick() {
            this.showArchiveDialog = true;
        },

        async confirmArchive() {
            this.archiving = true;
            try {
                await Repository.archiveTournament(this.tournamentId);
                this.$emit('tournamentArchived', this.tournamentId);
                this.showArchiveDialog = false;
                
                // Show success message
                this.$emit('showMessage', {
                    text: this.$t('tournamentArchived'),
                    color: 'success'
                });

                // Force re-render by emitting event to parent
                setTimeout(() => {
                    this.$emit('forceRefresh');
                }, 2500);
                
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('Error archiving tournament:', error);
                this.$emit('showMessage', {
                    text: this.$t('errorArchivingTournament'),
                    color: 'error'
                });
            } finally {
                this.archiving = false;
            }
        },

        handleUnarchiveClick() {
            this.showUnarchiveDialog = true;
        },

        async confirmUnarchive() {
            this.archiving = true;
            try {
                await Repository.unarchiveTournament(this.tournamentId);
                this.$emit('tournamentArchived', this.tournamentId); // Reuse the same event
                this.showUnarchiveDialog = false;
                
                // Show success message
                this.$emit('showMessage', {
                    text: this.$t('tournamentUnarchived'),
                    color: 'success'
                });

                // Force re-render by emitting event to parent
                setTimeout(() => {
                    this.$emit('forceRefresh');
                }, 2500);
                
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('Error unarchiving tournament:', error);
                this.$emit('showMessage', {
                    text: this.$t('errorUnarchivingTournament'),
                    color: 'error'
                });
            } finally {
                this.archiving = false;
            }
        },

        goToTournament(tournamentId) {
            this.$router.push({
                name: 'tournament-overview',
                query: { tournamentId: Number.parseInt(tournamentId) }
            });
        },
        dhm(ms) {
            const days = Math.floor(ms / (24 * 60 * 60 * 1000));
            const daysms = ms % (24 * 60 * 60 * 1000);
            const hours = Math.floor(daysms / (60 * 60 * 1000));
            const hoursms = ms % (60 * 60 * 1000);
            const minutes = Math.floor(hoursms / (60 * 1000));
            const minutesms = ms % (60 * 1000);
            const sec = Math.floor(minutesms / 1000);
            return days + ":" + hours + ":" + minutes + ":" + sec;
        },

        // New method to get the display message with archived check
        getDisplayMessage() {
            // Check if tournament is archived first
            if (this.tournamentVisibility === 'ARCHIVED') {
                return {
                    text: this.$t('archived'),
                    color: 'bg-archived',
                    icon: null
                };
            }
            
            // Otherwise return the normal tournament message state
            return this.tournamentMsgState;
        },

        // New method to get the display message color
        getDisplayMessageColor() {
            if (this.tournamentVisibility === 'ARCHIVED') {
                return 'bg-archived';
            }
            
            return this.tournamentMsgState.color;
        },

    }
}
</script>

<style lang="scss">
$swt-success: var(--v-mainGreen-base);
$swt-error: #C42A14;
$swt-warning: #FF8933;
$swt-yellow: #FFF75F;
$swt-light-gray: #EEEEEE;
$swt-dark-gray: #303740;

.theme--dark {
    .tournament-card {
        background-color: #232830;

        .card-divider {
            background-color: #303740;
        }

        &.tournament-card--allVariation {
            .tag-header {
                &.tag-header--bg-gray {
                    background-color: $swt-dark-gray;
                    color: var(--v-mainBlack-base);
                }

                &.tag-header--bg-archived {
                    background-color: #6c757d;
                    color: var(--v-mainWhite-base);
                }
            }

            .tournament-timer {

                .tournament-timer__ended-text,
                .timer-box {
                    background-color: $swt-dark-gray;
                }

                .timer-box {
                    &.bg-yellow {
                        background-color: #FFC123;
                    }
                }
            }
        }

        &.tournament-card--enrolledVariation {
            .tournament-timer {
                background-color: $swt-dark-gray;
            }
        }
    }
}

.tournament-card {
    background-color: var(--v-cardBackground-base);
    border-radius: 15px;
    padding: 0px 16px 19px 16px;
    display: flex;
    flex-direction: column;
    transition: all ease-in-out 0.1s;
    font-family: "Avenir", sans-serif;
    width: 390px;
    box-shadow: 5px 5px 20px 0px rgba(0, 0, 0, 0.05);
    position: relative;

    .card-menu-container {
        position: absolute;
        top: 20px;
        right: 16px;
        z-index: 2;

        .card-menu-dots {
            cursor: pointer;
            color: var(--v-mainBlack-base);
            font-size: 18px;
            padding: 4px;

            &--disabled {
                opacity: 0.3;
                cursor: default;
            }
        }
    }

    .card-divider {
        height: 1px;
        background-color: #ebebeb;
        margin: 0 0 12px 0;
    }

    .tournament-card-card__title,
    .tournament-info,
    .tournament-timer {
        cursor: pointer;
    }

    &.tournament-card--allVariation {
        @media (max-width: 768px) {
            width: 88vw;
        }

        border: 1px solid transparent;
        position: relative;

        .tag-header {
            border-top-right-radius: 20px;
            border-bottom-right-radius: 20px;
            padding: 9px 22px;
            position: absolute;
            left: 0;
            top: 27px;

            &.tag-header--bg-gray {
                background-color: $swt-light-gray;
                color: var(--v-mainBlack-base);
            }

            &.tag-header--bg-black {
                background-color: var(--v-mainBlack-base);
                color: var(--v-mainWhite-base);
            }

            &.tag-header--bg-archived {
                background-color: #6c757d;
                color: var(--v-mainWhite-base);
            }
        }

        .tournament-card-card__title {
            padding-top: 81px;
            padding-bottom: 10px;
            overflow: hidden;
            text-overflow: ellipsis;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            white-space: initial;
            -webkit-box-orient: vertical;
        }

        .tournament-info {
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            padding-bottom: 20px;

            .tournament-info__text {
                display: flex;
                align-items: center;
                flex-direction: row;
                padding: 2.5px 5px;
                font-size: 14px;
                color: #ababab !important;

                .tournament-info__icon {
                    padding-right: 5px;
                    color: #ababab !important;
                }
            }
        }

        .tournament-timer {
            display: flex;

            &.tournament-timer--box-type {
                justify-content: space-between;
                flex-wrap: nowrap;
            }

            .timer-box {
                border-radius: 5px;
                padding: 5px 10px;
                background: $swt-light-gray;
                font-size: 14px;
                width: 62px;

                &.bg-green {
                    background-color: $swt-success;
                }

                &.bg-red {
                    background-color: $swt-error;
                }

                &.bg-yellow {
                    background-color: $swt-yellow;
                }

                &.bg-orange {
                    background: $swt-warning;
                }

                &.timer-state {
                    font-weight: bold;
                    display: flex;
                    align-items: center;
                    text-align: center;
                    font-size: 12px;
                    letter-spacing: 0.5px;
                }

                &.bg-orange {
                    background: $swt-warning;
                    color: #fff;
                }
            }

            .tournament-timer__number {
                display: flex;
                flex-direction: column;
                text-align: center;
                font-size: 2rem;

                span {
                    font-size: 14px;
                }
            }

            .tournament-timer__ended-text {
                font-size: 14px;
                border-radius: 5px;
                width: 100%;
                background: $swt-light-gray;
                text-align: center;
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 25px;
            }
        }

        &:hover {
            border: 1px solid #9A9A9A;
            box-shadow: none;
        }
    }

    &.tournament-card--enrolledVariation {

        height: fit-content;
        border: 1px solid transparent;

        .tag-header {
            width: fit-content;
            padding: 5px 10px;
            border: 1px solid;
            border-radius: 30px;
            margin-top: 20px;
            margin-bottom: 20px;

            i {
                margin-right: 5px;
            }

            &.tag-header--swt-success {
                color: $swt-success;
            }

            &.tag-header--swt-error {
                color: $swt-error;
            }

            &.tag-header--swt-warning {
                color: $swt-warning;
            }

        }

        .tournament-card-card__title {
            padding-bottom: 10px;
            height: 87px;
            overflow: hidden;
            text-overflow: ellipsis;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            white-space: initial;
            -webkit-box-orient: vertical;
        }

        .tournament-timer {
            text-align: center;
            font-size: 14px;
            font-weight: bold;
            padding: 5px 0px;
            background: $swt-light-gray;
            border-radius: 6px;
            width: 230px;

            i {
                margin-right: 10px;
            }
        }

        &:hover {
            border: 1px solid #9A9A9A;
            box-shadow: none;
        }
    }
    &.tournament-card--hide-tag {
        &.tournament-card--allVariation {
            .tournament-card-card__title {
                padding-top: 19px;
                padding-right: 30px;
                min-height: 80px; // 2 lines of h2 (26px * ~1.5 line-height * 2) + padding
            }
        }
    }

    &:hover{
        cursor: pointer;
    }
}
</style>